<template>
  <div
    id="echartKLine"
    element-loading-background="rgba(23, 27, 43, 1)"
    :style="{ width: '100%', height: '100%' }"
  ></div>
</template>
<script>
import { mapState } from 'vuex'
import IndicatorHelper from '@/utils/indicatorHelper'
import { parseInterval } from '@/utils'
import { fetchContractHistoryKLine } from '@/api/contract'

import { sendWSPush, createSocket } from '@/utils/contractWebSocket'
let kLineChart = null // 保存 Echart 对象
export default {
  computed: {
    ...mapState('contract', [
      'klineData',
      'indicator',
      'interval',
      'contractData',
    ]),
    dateData() {
      return this.klineData.map((item) => item.getDate())
    },
    contractNo() {
      return this.contractData.contractNo
    },
  },
  watch: {
    interval() {
      if (kLineChart) {
        kLineChart.clear()
        kLineChart.showLoading()
      }
      this._sendWSPush()
    },
    contractNo(_, oldVal) {
      if (kLineChart) {
        kLineChart.clear()
        kLineChart.showLoading()
      }
      this._sendWSPush(oldVal)
    },
    klineData(newV) {
      if (!newV.length) return
      this.setEchart()
    },
    indicator() {
      if (kLineChart) {
        kLineChart.clear()
      }
      this.setEchart()
    },
  },
  mounted() {
    createSocket(process.env.VUE_APP_CONTRACT_WS)
    const domObj = document.getElementById('echartKLine')
    kLineChart = this.$echart.init(domObj)
    kLineChart.on('rendered', () => {
      kLineChart.hideLoading()
    })
    window.addEventListener('resize', this.resizeEcharts)

    if (!this.klineData.length) {
      this._sendWSPush()
    } else {
      this.setEchart()
    }
  },
  destroyed() {
    window.removeEventListener('resize', this.resizeEcharts)
    if (kLineChart && !kLineChart.isDisposed) {
      kLineChart.dispose()
      kLineChart = null
    }
  },
  methods: {
    async getContractHistoryKLine(params) {
      const data = await fetchContractHistoryKLine(params)

      this.$store.commit('contract/SET_KLINE_DATA', {
        data,
        symbol: params.market,
        type: params.type,
        isAll: true,
      })
    },
    async _sendWSPush(unsubMarket) {
      this.$store.commit('contract/CLEAR_KLINE_DATA')
      const { type, from, to } = parseInterval(this.interval, 500, true)
      if (unsubMarket) {
        sendWSPush({
          channel: 'kline',
          market: unsubMarket,
          type,
          subType: 'unsub',
        })
      }
      const subMarket = this.contractData.contractNo.toLowerCase()
      if (!this.klineData.length) {
        await this.getContractHistoryKLine({
          type,
          from,
          to,
          market: subMarket,
        })
      }
      sendWSPush({
        channel: 'kline',
        market: subMarket,
        type,
        subType: 'sub',
      })
    },
    resizeEcharts() {
      kLineChart && kLineChart.resize()
    },
    setEchart() {
      const series = IndicatorHelper.generateSeries(
        this.indicator,
        this.klineData
      )
      const formatter = IndicatorHelper.generateFormatter(this.indicator)
      const option = {
        grid: [
          {
            top: 20,
            right: 75,
            bottom: 145,
            left: 0,
          },
          {
            top: 314,
            right: 75,
            bottom: 36,
            left: 0,
          },
        ],
        xAxis: [
          {
            type: 'category',
            axisLine: {
              show: true,
              lineStyle: {
                color: '#979797',
              },
            },
            axisTick: {
              show: false,
            },
            axisLabel: {
              show: false,
            },
            // 隐藏该坐标轴指示器 label 属性
            axisPointer: {
              label: {
                show: false,
              },
            },
            data: this.dateData,
          },
          {
            type: 'category',
            gridIndex: 1,
            axisLine: {
              show: true,
              lineStyle: {
                color: '#979797',
              },
            },
            axisTick: {
              show: false,
            },
            axisLabel: {
              margin: 9,
              color: '#C7C7C7',
              fontSize: 12,
              fontWeight: 400,
            },
            axisPointer: {
              label: {
                show: true,
                margin: 5,
              },
            },
            data: this.dateData,
          },
        ],
        yAxis: [
          {
            type: 'value',
            scale: true,
            position: 'right',
            axisLine: {
              show: true,
              lineStyle: {
                color: '#979797',
              },
            },
            axisTick: {
              show: false,
            },
            splitLine: {
              show: false,
              lineStyle: {
                color: '#2E2E2E',
              },
            },
            axisLabel: {
              show: true,
              color: '#C7C7C7',
              fontSize: 12,
              fontWeight: 400,
              formatter: (val) => {
                const [_int, _digit] = String(val).split('.')
                return _digit ? `${_int}.${_digit.slice(0, 4)}` : _int
              },
            },
            min: function (value) {
              return value.min
            }, // eslint-disable-line
            max: function (value) {
              return value.max
            }, // eslint-disable-line
            // interval: 200 // 坐标轴分隔间隔
          },
          {
            type: 'value',
            gridIndex: 1,
            position: 'right',
            axisLine: {
              show: true,
              lineStyle: {
                color: '#979797',
              },
            },
            axisTick: {
              show: false,
            },
            splitLine: {
              show: false,
            },
            axisLabel: {
              show: true,
              color: '#C7C7C7',
              fontSize: 12,
              fontWeight: 400,
              formatter: (value, index) => {
                const unitSize = [
                  { value: 1, symbol: '' },
                  { value: 1000, symbol: 'K' }, // 千
                  { value: 1000000, symbol: 'M' }, // 百万
                  { value: 1000000000, symbol: 'B' }, // 十亿
                ]
                const len = unitSize.length
                let i
                for (i = len - 1; i > -1; i--) {
                  if (value >= unitSize[i].value) {
                    return (
                      (value / unitSize[i].value).toFixed(2) +
                      unitSize[i].symbol
                    )
                  }
                }
              },
            },
          },
        ],
        /* eslint-disable */
        visualMap: this.indicator.some((item) => ['VOL', 'MACD'].includes(item))
          ? {
              show: false,
              seriesIndex: 4,
              dimension: 2, // 取到 volumes 数组的第三列数据
              pieces: [
                {
                  value: 1,
                  color: '#41B37E', // 绿色
                },
                {
                  value: -1,
                  color: '#CE4444', // 红色
                },
              ],
            }
          : [],
        /* eslint-enable */
        tooltip: {
          trigger: 'axis',
          // tooltip 中便捷配置坐标轴指示器
          axisPointer: {
            type: 'cross',
            axis: 'x',
            lineStyle: {
              color: '#979797',
              type: 'dashed',
            },
            label: {
              color: '#42B3FA',
              fontWeight: 400,
              backgroundColor: '#171B2A',
              lineHeight: 22,
              // shadowBlur: 3
            },
          },
          position: [10, 0],
          padding: [0, 0, 0, 0],
          backgroundColor: 'transparent',
          formatter,
        },
        // 配置全局坐标轴指示器
        axisPointer: {
          link: { xAxisIndex: [0, 1] },
        },
        dataZoom: [
          {
            type: 'inside',
            xAxisIndex: [0, 1],
            start: 100,
            minValueSpan: 50,
          },
        ],
        series,
        animation: false,
      }
      kLineChart.setOption(option, false)
    },
  },
}
</script>
